import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MY_SURVEY_ANSWERS } from '../services/surveyQueries';
import { AreaSondaggi, codiceErrore, getSurveyClient, surveyOpContext } from '../services/surveyAuth';
import type { RispostaInviata, SondaggioRisposte } from '../components/survey/RisposteInviate';

/**
 * Risposte inviate a un sondaggio da chi ha effettuato l'accesso, in sola lettura.
 *
 * Identità come nella pagina di risposta, senza il link ricevuto per email
 * (le pagine stanno dietro l'accesso):
 *  - area riservata: Bearer associato (se valido) e P.IVA di `associate_data`.
 *    Il server usa il Bearer sui sondaggi con identità verificata e la P.IVA
 *    (che con il Bearer deve coincidere con quella del token) sugli altri: le
 *    due insieme coprono entrambi i casi senza conoscere prima il sondaggio;
 *  - area associativa: solo il Bearer rappresentante, P.IVA null.
 *
 * Il sondaggio arriva con la risposta (`mySurveyResponse.survey`): la query
 * `survey(id)` ai non admin restituisce solo i sondaggi attivi, mentre le
 * risposte restano consultabili anche a sondaggio chiuso.
 */

export type StatoRisposte = 'caricamento' | 'ok' | 'nessuna' | 'sessione' | 'errore';

export interface EsitoRisposte {
  stato: StatoRisposte;
  survey: SondaggioRisposte | null;
  response: RispostaInviata | null;
  /** Messaggio del server per i blocchi con codice (es. impersonificazione), altrimenti null */
  messaggio: string | null;
}

const leggiPartitaIVA = (): string | null => {
  try {
    return JSON.parse(localStorage.getItem('associate_data') || '{}').partitaIVA || null;
  } catch {
    return null;
  }
};

// Bearer rifiutato: 401 del contesto GraphQL o codice UNAUTHENTICATED
const sessioneRifiutata = (error: unknown): boolean =>
  codiceErrore(error) === 'UNAUTHENTICATED'
  || (error as { networkError?: { statusCode?: number } } | null)?.networkError?.statusCode === 401;

// Codici per cui il server spiega già il motivo (identità, pubblico, impersonificazione, carica)
const CODICI_CON_MESSAGGIO = [
  'IDENTITA_RICHIESTA', 'PUBBLICO_STUDI', 'PUBBLICO_RAPPRESENTANTI',
  'RAPPRESENTANTE_SENZA_PERMESSO',
];

export const MESSAGGIO_IMPERSONIFICAZIONE =
  'Le risposte dei sondaggi con identità verificata non sono visibili in impersonificazione';

export function useRisposteInviate(id: string | undefined, area: AreaSondaggi): EsitoRisposte {
  const associativa = area === 'associativa';
  const [identita] = useState(() => ({
    headers: id ? surveyOpContext(id, { area }).headers : undefined,
    partitaIVA: associativa ? null : leggiPartitaIVA(),
  }));
  const haIdentita = !!identita.headers || !!identita.partitaIVA;

  const { data, loading, error } = useQuery(GET_MY_SURVEY_ANSWERS, {
    client: getSurveyClient(),
    context: identita.headers ? { headers: identita.headers } : undefined,
    variables: { surveyId: id, partitaIVA: identita.partitaIVA, linkToken: null },
    skip: !id || !haIdentita,
    fetchPolicy: 'network-only',
  });

  const vuoto = { survey: null, response: null, messaggio: null };
  if (!id) return { stato: 'nessuna', ...vuoto };
  if (!haIdentita) return { stato: 'sessione', ...vuoto };
  if (error) {
    if (sessioneRifiutata(error)) return { stato: 'sessione', ...vuoto };
    const codice = codiceErrore(error);
    // Il messaggio del server parla di «compilazione»: qui si consultano le risposte
    const messaggio = codice === 'IMPERSONIFICAZIONE'
      ? MESSAGGIO_IMPERSONIFICAZIONE
      : codice && CODICI_CON_MESSAGGIO.includes(codice) ? error.message : null;
    return { stato: 'errore', ...vuoto, messaggio };
  }
  if (loading && !data) return { stato: 'caricamento', ...vuoto };

  const risposta = data?.mySurveyResponse;
  if (!risposta || risposta.status !== 'COMPLETED' || !risposta.survey) {
    return { stato: 'nessuna', ...vuoto };
  }
  return { stato: 'ok', survey: risposta.survey, response: risposta, messaggio: null };
}
